import { PerspectiveCamera, Vector3 } from 'three';
import { CAMERA } from '../config/tuning';

export interface CameraTarget {
  x: number;
  z: number;
  /** Road height under the camera (behind the car), and at the point it looks at ahead. */
  y: number;
  lookY: number;
  yaw: number;
  speed: number;
  /** Body slip angle (velocity direction relative to heading), radians. */
  slip: number;
  ax: number;
  offTrack: boolean;
  impact: number;
  /** The road's banking under the car (radians, left side up positive): the view leans with it, partway. */
  roll?: number;
  /** The moment is held (paused, a lesson's freeze): the car isn't going anywhere, so nothing shakes, bobs or jolts. */
  frozen?: boolean;
}

/** The player's system asks for less motion: the camera never bobs or jolts. */
const calm = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

/**
 * Smooth random motion in time: a new random point every `step` seconds, eased between them. Now and then (`knock`
 * of the points) one lands further out: a ripple in the tarmac.
 */
class Wobble {
  private from = 0;
  private to = 0;
  private u = 1;
  constructor(
    private step: number,
    private knock = 0,
  ) {}
  next(dt: number): number {
    this.u += dt / this.step;
    while (this.u >= 1) {
      this.u -= 1;
      this.from = this.to;
      this.to = (Math.random() * 2 - 1) * (Math.random() < this.knock ? 2.2 : 1);
    }
    const e = this.u * this.u * (3 - 2 * this.u);
    return this.from + (this.to - this.from) * e;
  }
}

/**
 * Chase camera: behind and slightly above, lagging smoothly, field of view
 * widening with speed, bobbing over rough ground and jolted by a wall.
 */
export class ChaseCamera {
  readonly camera: PerspectiveCamera;
  private yaw = 0;
  private dist: number = CAMERA.distance;
  private distV = 0;
  private bump = 0;
  /** A wall hit's push towards the car (m), and its speed: one movement, settling back. */
  private jolt = 0;
  private joltV = 0;
  private lastHit = -Infinity;
  private baseY = 0;
  private roll = 0;
  private lookBaseY = 0;
  private t = 0;
  private initialized = false;
  private tmp = new Vector3();
  aspect = 16 / 9;
  /** Screen shake (the pause poster's switch): off, the camera never bobs, jolts or shakes, as with reduced motion. */
  motion = true;
  // The speed shake's randomness, each way it turns: quick small moves, a slower drift under them, and its strength
  // rising and falling.
  private nodFine = new Wobble(1 / 14, 0.07);
  private nodSlow = new Wobble(1 / 5);
  private turnFine = new Wobble(1 / 12, 0.07);
  private turnSlow = new Wobble(1 / 4);
  private surge = new Wobble(0.8);

  constructor() {
    this.camera = new PerspectiveCamera(CAMERA.fovBase, 16 / 9, 0.3, 9000);
  }

  snap(): void {
    this.initialized = false;
  }

  update(tg: CameraTarget, dt: number): void {
    const h = Math.min(dt, 1 / 20);
    this.t += h;
    const target = tg.yaw + tg.slip * CAMERA.slipLook;
    if (!this.initialized) {
      this.yaw = target;
      this.dist = CAMERA.distance;
      this.distV = 0;
      this.baseY = tg.y;
      this.lookBaseY = tg.lookY;
      this.initialized = true;
    }
    // Hills: the camera rides up and down with the road, a touch behind it.
    this.baseY += (tg.y - this.baseY) * (1 - Math.exp(-10 * h));
    this.lookBaseY += (tg.lookY - this.lookBaseY) * (1 - Math.exp(-10 * h));
    let d = target - this.yaw;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    this.yaw += d * (1 - Math.exp(-CAMERA.yawStiffness * h));

    // The camera drops back a little under acceleration, closes in under braking.
    // At speed it comes in closer (CAMERA.speedPull) and lower (speedSink).
    const pace = Math.min(tg.speed, 60);
    const dTarget = CAMERA.distance + Math.max(-0.6, Math.min(0.6, tg.ax * CAMERA.accelPull)) - pace * CAMERA.speedPull;
    const a = CAMERA.distanceSpring * CAMERA.distanceSpring * (dTarget - this.dist) - 2 * CAMERA.distanceSpring * this.distV;
    this.distV += a * h;
    this.dist += this.distV * h;

    const fx = Math.cos(this.yaw);
    const fz = -Math.sin(this.yaw);
    const cam = this.camera;
    const sink = CAMERA.speedSink * (pace / 60) ** 2;
    cam.position.set(tg.x - fx * this.dist, this.baseY + CAMERA.height - sink, tg.z - fz * this.dist);
    this.tmp.set(tg.x + fx * CAMERA.lookAhead, this.lookBaseY + CAMERA.lookHeight, tg.z + fz * CAMERA.lookAhead);

    // Rough ground and walls move the view slowly, never in a rattle: big movements more than three times a second
    // can bring on a seizure. Grass rocks it gently up and down; a wall throws it once towards the car (a wall
    // scraped along doesn't keep at it). Only the speed shake below is quick, and it's about a pixel (the user's
    // choice, 10 Oct 2026). None of it with the pause poster's "screen shake" off, or for a player whose system
    // asks for less motion.
    this.bump += ((tg.offTrack ? CAMERA.bumpGrass : 0) - this.bump) * (1 - Math.exp(-6 * h));
    if (tg.impact > 1 && this.t - this.lastHit > CAMERA.joltGap) {
      this.joltV += Math.min(CAMERA.joltMax, tg.impact * CAMERA.jolt);
      this.lastHit = this.t;
    }
    const w = CAMERA.joltSpring;
    this.joltV += (-w * w * this.jolt - 1.5 * w * this.joltV) * h;
    this.jolt += this.joltV * h;
    let nod = 0;
    let turn = 0;
    if (this.motion && !calm?.matches && !tg.frozen) {
      const bob = (Math.sin(this.t * 12) * 0.7 + Math.sin(this.t * 7.5 + 1.3) * 0.3) * this.bump * Math.min(1, tg.speed / 20);
      cam.position.set(cam.position.x + fx * this.jolt, cam.position.y + bob - this.jolt * 0.3, cam.position.z + fz * this.jolt);
      // The speed shake: a fine, random jitter that grows with speed, its strength surging and easing, a knock now
      // and then, the camera turning a hair: never moving (that made the car seem to wobble on the road) and never
      // rolling (the horizon stays level).
      const a = (pace / 60) ** 2 * (1 + 0.45 * this.surge.next(h));
      nod = a * CAMERA.shakePitch * (this.nodFine.next(h) * 0.7 + this.nodSlow.next(h) * 0.3);
      turn = a * CAMERA.shakeYaw * (this.turnFine.next(h) * 0.7 + this.turnSlow.next(h) * 0.3);
    }
    cam.lookAt(this.tmp);
    if (nod !== 0) cam.rotateX(nod);
    if (turn !== 0) cam.rotateY(turn);
    // A banked curve leans the view (a share of the road's tilt, eased in and out).
    this.roll += ((tg.roll ?? 0) * 0.45 - this.roll) * (1 - Math.exp(-6 * h));
    if (Math.abs(this.roll) > 1e-4) cam.rotateZ(-this.roll);

    const k = Math.min(1, tg.speed / CAMERA.fovSpeed);
    let fov = CAMERA.fovBase + (CAMERA.fovMax - CAMERA.fovBase) * k * k;
    // Portrait screens: keep enough horizontal view.
    if (this.aspect < 1.2) fov = Math.min(95, fov * (1.2 / this.aspect) ** 0.6);
    if (Math.abs(cam.fov - fov) > 0.01) {
      cam.fov = fov;
      cam.updateProjectionMatrix();
    }
  }

  /** Slow orbit around the car (title screen); on wide screens the car sits right of the title card. */
  orbit(x: number, y: number, z: number, yaw: number, t: number): void {
    const a = yaw + Math.PI * 0.72 + Math.sin(t * 0.11) * 0.55;
    const r = 7.8;
    const cx = x + Math.cos(a) * r;
    const cz = z - Math.sin(a) * r;
    this.camera.position.set(cx, y + 2.2, cz);
    // Look past the car, to its left on screen when the card is on the left.
    const side = this.aspect > 1.3 ? 3.2 : 0;
    const fx = x - cx;
    const fz = z - cz;
    const l = Math.hypot(fx, fz) || 1;
    // Screen-right is (-fz, 0, fx); look to the car's left so it sits right of centre.
    this.camera.lookAt(x + (fz / l) * side, y + 1.0, z - (fx / l) * side);
    this.initialized = false;
  }
}
